import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useSignal, useSignalEffect } from "@preact/signals";
import pluralize from "pluralize";
import { Link } from "react-router-dom";
import { ShareAction, useApp } from "~/core";
import { me } from "~/core/store";
import { Dialog, FilesPackPreview, IconButton } from "~/components";
import NextIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import ReceiptIcon from "@material-design-icons/svg/outlined/receipt_long.svg";

const ShareScreen: FC = () => {
    const { share, dialog, snack } = useApp();
    const isSelect = useSignal(false);
    const count = share.sharedFiles.value.length;
    const actions = [
        {
            title: "Create Expense",
            action: ShareAction.CreateExpense,
            description: `This will create a new expense with ${count} ${pluralize("receipt", count)}, one for each of the shared files.`,
            button: "Create",
            icon: ReceiptIcon,
            to: me("admin/expenses"),
        },
        {
            title: "Update Expense",
            action: ShareAction.UpdateExpense,
            description: `This will add ${count} ${pluralize("receipt", count)} to an existing expense.`,
            button: "Choose Expense",
            icon: ReceiptIcon,
            to: me("admin/expenses"),
        },
    ];

    useEffect(() => {
        if (share.error.value) {
            snack.error("Sharing files failed", share.error.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [share.error.value]);

    useSignalEffect(() => {
        if (
            (share.sharedFiles.value.length > 0 || share.loading.value) &&
            !share.clicked.value
        ) {
            isSelect.value = true;
        } else {
            isSelect.value = false;
        }
    });

    useSignalEffect(() => {
        dialog.isBackdrop.value = isSelect.value;
    });

    return (
        <Dialog show={isSelect} centered={false} noUrlState zIndex={101}>
            <div className="flex flex-col p-4">
                <div className="flex justify-end -mt-16 pb-16">
                    <IconButton
                        aria-label="Cancel"
                        flat
                        onClick={() => (isSelect.value = false)}
                    >
                        <CloseIcon width="20" height="20" />
                    </IconButton>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-slate-600 to-slate-700 p-4 text-sm text-white">
                    <p>
                        You have shared files with Engage, you can review and
                        choose what to do with them.
                    </p>
                    <p className="mt-2">
                        Shared files ({share.sharedFiles.value.length}):
                    </p>
                </div>
                <FilesPackPreview
                    files={share.sharedFiles}
                    className="-mx-4 mt-4 flex max-w-[28rem] gap-x-4 overflow-x-auto p-4"
                    itemClassName="size-32"
                />
                <p className="mt-4 text-sm">
                    Please select an action below to proceed with the shared
                    files.
                </p>
                <div className="mt-2 flex max-h-[calc(100vh-25rem)] flex-col gap-y-4 overflow-y-auto">
                    {actions.map(
                        ({
                            to,
                            icon: Icon,
                            title,
                            description,
                            button,
                            action,
                        }) => (
                            <Link
                                key={to}
                                to={to}
                                onClick={() => {
                                    share.click(action);
                                }}
                                className="flex flex-col rounded-3xl rounded-bl-none bg-gradient-to-br from-slate-600 to-slate-700 p-4 text-white outline-none"
                            >
                                <div className="mb-4 flex items-center gap-x-4">
                                    <Icon className="fill-current" />
                                    <h3 className="text-lg">{title}</h3>
                                </div>
                                <div>{description}</div>
                                <div className="mt-2 flex justify-end">
                                    <div className="flex items-center gap-x-2 rounded-2xl bg-white/10 px-4 py-2">
                                        <span>{button}</span>
                                        <NextIcon
                                            width="20"
                                            height="20"
                                            className="fill-current"
                                        />
                                    </div>
                                </div>
                            </Link>
                        ),
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default ShareScreen;
